import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import Badge from './Badge';
import CheckBox from './CheckBox';
import { PiUserLight, PiBuildingLight, PiCaretUp, PiCaretDown, PiSpinnerGapLight } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import ClientAvatar from '../../Clients/ClientAvatar';
import ProfileImage from '../../Header/ProfileImage';
import Button from './Button';
import Skeleton from './Skeleton';
import EmptyState, { EmptyStateProps } from './EmptyState';
import Spinner from './Spinner';
import TablePagination, { TablePaginationProps } from './TablePagination';


/**
 * Grow the declared column widths until they exactly fill the container.
 *
 * `table-layout: fixed` + one auto-width column means the auto column swallows
 * ALL the surplus: a 1400px container with ~600px of declared widths left the
 * data columns bunched in the left half and ~700px of dead band in the last
 * data column, right before the pinned Actions gutter. The surplus is instead
 * shared out here, proportionally, so every column grows and the auto column is
 * left with nothing but the rounding remainder.
 *
 * Exported for the unit tests: the browser is Auth0-gated, so the colgroup math
 * is asserted directly.
 */
export const fitColumnWidths = (
  displayColumns: string[],
  widths: Record<string, number>,
  availableWidth: number,
  opts: {
    isFixedColumn: (col: string) => boolean;
    fixedColumnWidth: (col: string) => number;
    minWidth: (col: string) => number;
    flexColumn?: string;
  }
): Record<string, number> => {
  const flexible = displayColumns.filter((c) => !opts.isFixedColumn(c));
  if (flexible.length === 0 || availableWidth <= 0) return widths;

  const fixedTotal = displayColumns
    .filter(opts.isFixedColumn)
    .reduce((sum, c) => sum + opts.fixedColumnWidth(c), 0);
  const budget = availableWidth - fixedTotal;
  const declared = flexible.map((c) => widths[c] ?? opts.minWidth(c));
  const sum = declared.reduce((a, b) => a + b, 0);

  // Declared widths already meet or exceed the container: leave them verbatim
  // so the table keeps its horizontal scroll instead of squashing.
  if (sum <= 0 || budget <= 0 || budget - sum < 1) return widths;

  const scale = budget / sum;
  const next = { ...widths };
  flexible.forEach((c, i) => {
    next[c] = Math.max(opts.minWidth(c), Math.floor(declared[i] * scale));
  });

  // The flex column takes the rounding remainder so the total lands exactly on
  // the budget — a second pass then sees no surplus and stops.
  const absorber = opts.flexColumn && flexible.includes(opts.flexColumn) ? opts.flexColumn : flexible[flexible.length - 1];
  const after = flexible.reduce((s, c) => s + next[c], 0);
  next[absorber] = Math.max(opts.minWidth(absorber), next[absorber] + (budget - after));

  return next;
};

interface TableProps {
  columns: string[];
  data: Record<string, any>[];
  hiddenColumns?: Set<string>;
  onRowClick?: (row: any) => void;
  renderRow?: (row: any, rowIndex: number) => React.ReactNode;
  renderCell?: (column: string, value: any, row: any) => React.ReactNode;
  enableSelection?: boolean;
  selectedIds?: string[];
  onSelect?: (id: string) => void;
  onSelectAll?: () => void;
  max?: string;
  min?: string;
  actions?: { label: string; onClick: (row: any) => void; icon: React.ReactNode; disabled?: (row: any) => boolean; isDestructive?: boolean; hidden?: (row: any) => boolean }[];
  hasMore?: boolean;
  isLoadingMore?: boolean;
  /** First-load state — renders the built-in skeleton inside the table chrome. */
  isLoading?: boolean;
  /** Rows for the built-in skeleton. Default 6. */
  skeletonRows?: number;
  onLoadMore?: () => void;
  emptyState?: EmptyStateProps;
  sortableColumns?: string[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  /** @deprecated Feed the count to `FilterBarWithTabs` instead. Inert here. */
  total_items?: number;
  /** @deprecated Feed the count to `FilterBarWithTabs` instead. Inert here. */
  total_showing_items?: number,
  /** @deprecated The count row is gone; the filter bar draws it. Inert. */
  setIsShowing?: boolean;
  /** @deprecated Use `itemCountLabel` on `FilterBarWithTabs`. Inert here. */
  showingLabel?: string;
  selectedCount?: number;
  resizableColumns?: boolean;
  defaultColumnWidths?: Record<string, number>;
  minColumnWidths?: Record<string, number>;
  maxColumnWidths?: Record<string, number>;
  /** Stable id used in the resize-persistence key `table:<route>:<tableId>`. Defaults to the first column key. */
  tableId?: string;
  storageKeyPrefix?: string;
  rowSize?: 'compact' | 'default' | 'large';
  /** @deprecated The row that drew this is gone. Render it beside the bar. */
  infoText?: string;
  /**
   * Server-side pagination. When given, the table grows a pager under its
   * scroll region and the "Showing" badge reports the row RANGE this page
   * covers rather than a running count — a page-at-a-time list showing
   * "Showing 50 of 1037" on page 3 is simply wrong.
   *
   * Mutually exclusive with `hasMore`/`onLoadMore` by intent: a list either
   * accumulates pages or replaces them, and a control for each would let a
   * user do both and see neither count add up.
   */
  pagination?: TablePaginationProps;
}

/**
 * A column heading capitalises its FIRST LETTER, not every word.
 *
 * This was `capitalize`, which is per-word: a heading written "Has access"
 * came out "Has Access" no matter how it was spelled at the call site, so a
 * two-word heading could never be sentence case. `first-letter:uppercase`
 * still fixes a lowercase key ("name" → "Name"), which is all the old rule was
 * defending against, and leaves the rest of the words as the caller wrote them.
 *
 * `inline-block` because `::first-letter` only applies to block containers —
 * on a bare inline span the rule does nothing at all.
 */
const HEADER_TEXT_CLASS = 'inline-block first-letter:uppercase whitespace-nowrap truncate';

const Table: React.FC<TableProps> = ({
  columns,
  data,
  hiddenColumns = new Set(),
  onRowClick,
  renderRow,
  enableSelection = false,
  selectedIds = [],
  onSelect,
  onSelectAll,
  /*
    The scroll region fills the flex box it is given (`flex-1 min-h-0`). `max`
    stays a viewport-relative cap so a table dropped on an unbounded page still
    scrolls its own rows and keeps its sticky head; `min` is 0 by default
    because a forced `min-h-[calc(100vh-150px)]` makes a ten-row list as tall
    as the viewport — the dead band you can scroll into below the last row, and
    the reason the pager underneath it lands off the bottom of the page.
  */
  /*
    150px → 112px: the count row is gone.

    That row was ~38px of THIS component, drawn above the capped region, so
    the box was cap + 38. Deleting it without moving the cap would have left
    those 38px as empty page below the last row on every table still sized by
    the guess rather than by a flex chain. The subtrahend goes down by exactly
    what was removed, and the footprint is unchanged.
  */
  max = 'max-h-[calc(100vh-112px)]',
  min = 'min-h-0',
  actions,
  hasMore,
  isLoadingMore,
  isLoading = false,
  skeletonRows = 6,
  onLoadMore,
  emptyState,
  sortableColumns = [],
  sortBy,
  sortOrder,
  onSort,
  total_items,
  total_showing_items,
  setIsShowing = false,
  showingLabel,
  selectedCount,
  resizableColumns = true,
  defaultColumnWidths = {},
  minColumnWidths = {},
  maxColumnWidths = {},
  tableId,
  renderCell,
  storageKeyPrefix = '',
  rowSize = 'default',
  infoText,
  pagination,
}) => {
  // Resize constraints (#2330): default min 40px, default max 800px; action /
  // selection columns get a 56px floor.
  const DEFAULT_MIN_WIDTH = 40;
  const DEFAULT_MAX_WIDTH = 800;
  const ACTION_MIN_WIDTH = 56;
  /**
   * The checkbox column is a fixed gutter, not a data column: a checkbox and
   * nothing else, hard against the left edge. It was 100px wide because the
   * width initialiser ran every declared default through `Math.max(100, …)`,
   * so `'Select': 48` became 100 and was then persisted. Select and Actions
   * are exempt from that floor, from resizing, and from stored widths.
   */
  const SELECT_WIDTH = 36;
  const isFixedColumn = (col: string): boolean => col === 'Select' || col === 'Actions';
  const fixedColumnWidth = (col: string): number => (col === 'Select' ? SELECT_WIDTH : ACTION_MIN_WIDTH);
  const colMinWidth = (col: string): number =>
    isFixedColumn(col) ? fixedColumnWidth(col) : (minColumnWidths[col] ?? DEFAULT_MIN_WIDTH);
  const colMaxWidth = (col: string): number => maxColumnWidths[col] ?? DEFAULT_MAX_WIDTH;
  // Persistence key: `table:<route>:<tableId>` (tableId defaults to first column).
  const resolvedTableId = String(tableId || storageKeyPrefix || columns[0] || 'table').replace(/[^a-zA-Z0-9_-]/g, '_');
  const tableRoute = (typeof window !== 'undefined' ? window.location.pathname : '').replace(/[^a-zA-Z0-9/_-]/g, '') || '/';
  const widthsStorageKey = `table:${tableRoute}:${resolvedTableId}`;
  const { t } = useTranslation();
  const [menuState, setMenuState] = useState<{ id: string | null; above: boolean; top: number; left: number; ready: boolean }>({ id: null, above: false, top: 0, left: 0, ready: false });
  const menuBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const menuRef = useRef<HTMLDivElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const resizingColRef = useRef<string | null>(null);
  const startXRef = useRef(0);

  // Initialize column widths from localStorage or defaults
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    if (!resizableColumns) return {};

    const stored = localStorage.getItem(widthsStorageKey);

    if (stored) {
      try {
        const parsed = JSON.parse(stored) as Record<string, number>;
        // A browser that used these tables before has a 100px Select stored.
        // The gutter columns are never user-sized, so their stored value is
        // discarded rather than migrated.
        Object.keys(parsed).forEach((col) => {
          if (isFixedColumn(col)) parsed[col] = fixedColumnWidth(col);
        });
        return parsed;
      } catch (e) {
        console.error('Failed to parse stored column widths:', e);
      }
    }

    // Use provided defaults or calculate proportional widths
    const displayColumns = enableSelection && !columns.includes('Select')
      ? ['Select', ...columns]
      : columns;

    const defaults: Record<string, number> = {};
    displayColumns.forEach((col) => {
      if (isFixedColumn(col)) {
        defaults[col] = fixedColumnWidth(col); // Fixed gutter for checkboxes and actions
      } else if (defaultColumnWidths[col]) {
        // Add margin to the provided default width (reduce slightly from provided value)
        defaults[col] = Math.max(100, defaultColumnWidths[col] - 20);
      } else {
        // Calculate width based on header text length with margin
        // Rough estimate: ~8px per character + 24px padding + 16px margin
        const estimatedWidth = col.length * 8 + 40;
        defaults[col] = Math.max(100, estimatedWidth);
      }
    });

    return defaults;
  });

  // Save column widths to localStorage
  const saveColumnWidths = (widths: Record<string, number>) => {
    if (!resizableColumns) return;
    localStorage.setItem(widthsStorageKey, JSON.stringify(widths));
  };

  // Handle resize start (plain pointer events — also covers touch/pen).
  const handleResizeStart = (e: React.PointerEvent, column: string) => {
    if (!resizableColumns) return;
    e.preventDefault();
    setIsResizing(true);
    resizingColRef.current = column;
    startXRef.current = e.clientX;
  };

  // Handle resize move
  useEffect(() => {
    if (!isResizing || !resizingColRef.current) return;

    const handlePointerMove = (e: PointerEvent) => {
      if (!tableRef.current) return;

      const delta = e.clientX - startXRef.current;
      const currentCol = resizingColRef.current;

      // Type guard: ensure currentCol is not null
      if (!currentCol) return;

      setColumnWidths((prev) => {
        const newWidths = { ...prev };
        const currentWidth = newWidths[currentCol] || 0;
        const minWidth = colMinWidth(currentCol);
        const maxWidth = colMaxWidth(currentCol);
        let newWidth = Math.max(minWidth, currentWidth + delta);
        newWidth = Math.min(maxWidth, newWidth);

        // Only update the resized column — keep all others exactly as they are
        // so they don't shift when the table total width changes
        newWidths[currentCol] = newWidth;

        return newWidths;
      });

      startXRef.current = e.clientX;
    };

    const handlePointerUp = () => {
      setIsResizing(false);
      resizingColRef.current = null;

      // Save widths on resize end
      setColumnWidths((prev) => {
        saveColumnWidths(prev);
        return prev;
      });
    };

    if (isResizing) {
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      return () => {
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
      };
    }
  }, [isResizing, resizableColumns]);

  // Infinite scroll disabled - using explicit "Load More" button instead
  // useEffect(() => {
  //   if (!hasMore || isLoadingMore) return;
  //
  //   const handleScroll = () => {
  //     const container = tableContainerRef.current;
  //     if (!container) return;
  //
  //     const scrollTop = container.scrollTop;
  //     const scrollHeight = container.scrollHeight;
  //     const clientHeight = container.clientHeight;
  //     const scrollThreshold = scrollHeight - clientHeight - 300;
  //
  //     if (scrollTop >= scrollThreshold && hasMore && !isLoadingMore) {
  //       onLoadMore?.();
  //     }
  //   };
  //
  //   const container = tableContainerRef.current;
  //   if (container) {
  //     container.addEventListener('scroll', handleScroll);
  //     return () => container.removeEventListener('scroll', handleScroll);
  //   }
  // }, [hasMore, isLoadingMore]);

  useLayoutEffect(() => {
    if (!menuState.id) return;
    if (menuBtnRefs.current[menuState?.id] && menuRef.current) {
      const btn = menuBtnRefs.current[menuState?.id]!;
      const rect = btn.getBoundingClientRect();
      const menuHeight = menuRef.current.offsetHeight;
      const menuWidth = menuRef.current.offsetWidth;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const above = spaceBelow < menuHeight && rect.top > menuHeight;
      const top = above ? rect.top - menuHeight - 4 : rect.bottom + 4;
      const left = Math.max(8, rect.right - menuWidth);
      setMenuState((prev) => ({ ...prev, above, top, left, ready: true }));
    }
  }, [menuState.id]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const isClickInsideMenu = menuRef.current?.contains(target);
      const isClickOnAnyButton = Object.values(menuBtnRefs.current).some(
        (btn) => btn && btn.contains(target)
      );

      if (!isClickInsideMenu && !isClickOnAnyButton) {
        setMenuState({ id: null, above: false, top: 0, left: 0, ready: false });
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Close menu on scroll since it's fixed-positioned via portal
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container || !menuState.id) return;
    const handleScroll = () => {
      setMenuState({ id: null, above: false, top: 0, left: 0, ready: false });
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [menuState.id]);

  const displayColumns = enableSelection
    ? columns.includes('Select')
      ? columns
      : ['Select', ...columns]
    : columns;

  // With `table-layout: fixed` and `width: max(100%, Σcols)`, any surplus (Σcols
// under the container width) is redistributed by the browser across EVERY column
  // that declares a width — including the 56px Select checkbox column, so resizing
  // one column visibly shifted the leftmost selector. Fix: leave exactly one
  // column (the last data column) auto-width so it absorbs the entire surplus;
  // every other column, Select included, then keeps its declared width verbatim.
  const flexColumn = resizableColumns
    ? [...displayColumns].reverse().find((c) => c !== 'Select' && c !== 'Actions')
    : undefined;

  /**
   * …and the surplus has to be shared out before that happens, or the one
   * auto-width column keeps the whole of it and reads as a dead band between
   * the last data column and the Actions gutter. Declared widths are content
   * estimates (~100px a column); they know nothing about the container, so they
   * are scaled up to it here — on mount and whenever the container resizes.
   */
  const displayColumnsKey = displayColumns.join('|');
  useLayoutEffect(() => {
    if (!resizableColumns) return;
    const el = tableContainerRef.current;
    if (!el) return;

    const fit = () => {
      const available = el.clientWidth;
      if (!available) return;
      setColumnWidths((prev) =>
        fitColumnWidths(displayColumns, prev, available, {
          isFixedColumn,
          fixedColumnWidth,
          minWidth: colMinWidth,
          flexColumn,
        })
      );
    };

    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizableColumns, displayColumnsKey, flexColumn]);

  /**
   * The table's minimum width — only the columns actually on screen. Summing
   * `columnWidths` wholesale counted stale keys left in localStorage by columns
   * this table no longer renders, which pushed the minimum past the container
   * and produced a phantom horizontal scroll.
   */
  const declaredTotalWidth = displayColumns.reduce(
    (sum, col) => sum + (isFixedColumn(col) ? fixedColumnWidth(col) : (columnWidths[col] ?? colMinWidth(col))),
    0
  );

  const sortedActions = actions
    ? [...actions].sort((a, b) => (a.isDestructive === b.isDestructive ? 0 : a.isDestructive ? 1 : -1))
    : [];


  const handleSortClick = (column: string) => {
    if (!sortableColumns.includes(column) || !onSort) return;
    const mappedSortBy = column.toLowerCase().replace(/\s+/g, '_');
    const newSortOrder = sortBy === mappedSortBy && sortOrder === 'asc' ? 'desc' : 'asc';
    onSort(mappedSortBy, newSortOrder);
  };


  /**
   * What the "Showing" badge says. A load-more list accumulates, so a count is
   * right. A paged list replaces, so the count has to be the RANGE of the whole
   * result set this page covers — otherwise page 3 of 1037 entries claims to be
   * showing the first 50.
   */

  return (
    <div className={`flex flex-col h-full w-full`}>
      {/* Same `px-3 py-2` gutter as the pagers below, so the head of a list
          and the foot of a list line up on the same edge. */}
      {/*
        NO COUNT ROW. "Showing x of x" moved into the FILTER BAR, on its left,
        where a list is narrowed — see `listCount` and `FilterBarWithTabs`.

        It cost this component a whole row of vertical space above the rows,
        for one short sentence, on every list page in the app. The table grows
        into that space now: the pages own a `flex-1 min-h-0` chain, so
        deleting the row hands its height straight to the scroll region rather
        than leaving a gap.

        The props that fed it (`total_items`, `total_showing_items`,
        `showingLabel`, `setIsShowing`, `infoText`) are kept and deprecated
        rather than removed: twenty-six pages pass them, and breaking all of
        them to delete five lines would be the tail wagging the dog. They are
        inert — the bar is fed directly.
      */}
      <div ref={tableContainerRef} className={clsx('flex-1 overflow-y-auto overflow-x-auto custom-scrollbar-main', max, min)} style={{ userSelect: isResizing ? 'none' : 'auto' }}>
        <table
          ref={tableRef}
          className={clsx(`divide-y divide-gray-200 dark:divide-gray-700`)}
          style={{
            borderCollapse: 'collapse',
            tableLayout: resizableColumns ? 'fixed' : 'auto',
            width: resizableColumns ? `max(100%, ${declaredTotalWidth}px)` : '100%',
          }}
        >
          {resizableColumns && (
            <colgroup>
              {displayColumns.map((col) => {
                // The gutter columns ignore both the stored width and the
                // declared default — they are always exactly their fixed width.
                if (isFixedColumn(col)) {
                  return <col key={col} style={{ width: `${fixedColumnWidth(col)}px` }} />;
                }
                // Exactly one column is left auto-width — the flex column, which
                // absorbs the leftover. Every other column falls back to its min
                // rather than to auto, so a stale stored map can't quietly make a
                // second column flexible and split the surplus in two.
                const w = col === flexColumn ? undefined : (columnWidths[col] ?? colMinWidth(col));
                return <col key={col} style={w ? { width: `${w}px` } : undefined} />;
              })}
            </colgroup>
          )}
          <thead className="bg-gray-200/70 dark:bg-gray-800/80 backdrop-blur-md sticky top-0 z-10">
            <tr>
              {displayColumns.map((col, idx) => {
                const colWidth = columnWidths[col];
                const isLastColumn = idx === displayColumns.length - 1;
                // Calculate proper minWidth based on column type
                const headerMinWidth = colMinWidth(col);

                return (
                  <th
                    key={col}
                    scope="col"
                    className={clsx(
                      'relative text-left text-xs text-gray-500 dark:text-gray-400 capitalize tracking-wider',
                      col === 'Select' ? 'px-0' : 'px-1 py-2.5',
                      col === 'Select' && 'py-2.5',
                      idx === 0 && !enableSelection && 'pl-4',
                      hiddenColumns.has(col) && 'hidden lg:table-cell',
                      col === 'Select' && !resizableColumns && 'w-12',
                    )}
                    style={isFixedColumn(col)
                      ? { width: `${fixedColumnWidth(col)}px`, minWidth: `${fixedColumnWidth(col)}px`, maxWidth: `${fixedColumnWidth(col)}px`, flexShrink: 0, ...(col === 'Select' ? { paddingLeft: '0px', paddingRight: '0px' } : {}) }
                      : col === flexColumn ? { minWidth: `${headerMinWidth}px` }
                      : colWidth ? { width: `${colWidth}px`, minWidth: `${headerMinWidth}px`, maxWidth: `${colMaxWidth(col)}px`, flexShrink: 0 } : { maxWidth: `${colMaxWidth(col)}px` }
                    }
                  >
                    <div className={clsx('flex items-center h-full', col === 'Select' ? 'justify-center w-full' : 'justify-between')}>
                      <div className={col === 'Select' ? 'flex items-center justify-center w-full' : 'flex-1 min-w-0 overflow-hidden'}>
                        {col === 'Select' && enableSelection ? (
                          <CheckBox
                            label={t('Select all')}
                            hideLabel
                            checked={selectedIds.length === data.length && data.length > 0}
                            onChange={() => onSelectAll && onSelectAll()}
                          />
                        ) : sortableColumns.includes(col) ? (
                          <button
                            onClick={() => handleSortClick(col)}
                            className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 min-w-0 max-w-full"
                          >
                            <span className={HEADER_TEXT_CLASS}>{t(col)}</span>
                            <div className="flex flex-col ml-1">
                              <PiCaretUp
                                className={clsx(
                                  'h-3 w-3',
                                  sortBy === col.toLowerCase().replace(/\s+/g, '_') && sortOrder === 'asc'
                                    ? 'text-gray-700 dark:text-gray-200 opacity-100'
                                    : 'text-gray-400 dark:text-gray-500 opacity-50'
                                )}
                              />
                              <PiCaretDown
                                className={clsx(
                                  'h-3 w-3',
                                  sortBy === col.toLowerCase().replace(/\s+/g, '_') && sortOrder === 'desc'
                                    ? 'text-gray-700 dark:text-gray-200 opacity-100'
                                    : 'text-gray-400 dark:text-gray-500 opacity-50'
                                )}
                              />
                            </div>
                          </button>
                        ) : col === 'Actions' ? (
                          null
                        ) : (
                          <span className={HEADER_TEXT_CLASS}>{t(col)}</span>
                        )}
                      </div>

                      {/* The flex column has no handle: it has no fixed width to drag. */}
                      {resizableColumns && !isLastColumn && col !== 'Select' && col !== 'Actions' && col !== flexColumn && (
                        <div
                          onPointerDown={(e) => handleResizeStart(e, col)}
                          className={clsx(
                            'table-resize-handle',
                            isResizing && resizingColRef.current === col && 'resizing'
                          )}
                          title={t('Drag to resize column')}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {isLoading && data.length === 0 ? (
              /* Built-in first-load skeleton — THE banded table-list loader.
                 Never wrap Table in a page-level skeleton. */
              <tr>
                <td colSpan={displayColumns.length} className="p-0">
                  <Skeleton variant="table" rows={skeletonRows} />
                </td>
              </tr>
            ) : (data.length === 0 && !isLoadingMore) ? (
              <tr className='my-auto'>
                <td colSpan={displayColumns.length} className="text-center">
                  {/* THE shared empty state — same component a pane or a grid
                      uses, so the three of them can never drift apart. */}
                  <EmptyState {...(emptyState || {})} />
                </td>
              </tr>
            ) : (
              data.map((row, rowIdx) => {
                if (renderRow) return renderRow(row, rowIdx);
                return (
                  <tr
                    key={row.id || rowIdx}
                    className={clsx(
                      'group cursor-pointer transition-colors duration-200',
                      rowIdx % 2 === 0 ? 'bg-white/75 dark:bg-gray-900/75' : 'bg-primary-50/70 dark:bg-gray-800/70',
                      'hover:bg-primary-100 dark:hover:bg-gray-700',
                      row.className
                    )}
                    style={row.style}
                    onClick={() => onRowClick && onRowClick(row)}
                  >
                    {displayColumns.map((col: any, colIdx: number) => {
                      let cellContent = row[col];

                      // Use custom renderCell if provided
                      if (renderCell) {
                        const customContent = renderCell(col, cellContent, row);
                        if (customContent !== undefined && customContent !== null) {
                          cellContent = customContent;
                        }
                      }

                      if (col === 'Select' && enableSelection) {
                        cellContent = (
                          <div
                            className="flex items-center justify-center w-full h-full"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <CheckBox
                              label={t('Select row')}
                              hideLabel
                              disabled={row.FolderType === 'Locked' ? true : false}
                              checked={selectedIds.includes(row.id)}
                              onChange={() => onSelect && onSelect(row.id)}
                            />
                          </div>
                        );
                      } else if (col === 'Client' && typeof row[col] === 'object') {
                        const { name, email, clientType, companyLogo } = row[col];
                        cellContent = (
                          <div className="flex items-center gap-2">
                            <ClientAvatar email={email} name={name} clientType={clientType} companyLogo={companyLogo} />
                            <div>
                              <div className="text-gray-900 dark:text-gray-200">{name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{email}</div>
                            </div>
                          </div>
                        );
                      } else if (col === 'Profile' && typeof row[col] === 'object') {
                        const { name, email, profilePicture, initials } = row[col];
                        cellContent = (
                          <div className="flex items-center gap-2">
                            <ProfileImage profilePicture={profilePicture} email={email} initials={initials} name={name} />
                            <div>
                              <div className="text-gray-900 dark:text-gray-200">{name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{email}</div>
                            </div>
                          </div>
                        );
                      } else if (col === 'Client Type') {
                        cellContent = (
                          <div className="flex items-center gap-2">
                            {row[col] === 'Business' ? (
                              <PiBuildingLight className="text-secondary-500" size={16} />
                            ) : (
                              <PiUserLight className="text-secondary-500" size={16} />
                            )}
                            <span>{t(row[col])}</span>
                          </div>
                        );
                      } else if (col === 'Value' && typeof row[col] === 'number') {
                        cellContent = <span className="">${row[col].toLocaleString()}</span>;
                      } else if (col === 'Case Count' && typeof row[col] === 'number') {
                        cellContent = <span className="">{row[col].toLocaleString()}</span>;
                      } else if (col === 'Actions' && sortedActions) {
                        const visibleActions = sortedActions.filter((action) => !(typeof action.hidden === 'function' && action.hidden(row)));
                        cellContent = visibleActions.length === 0 ? null : (
                          <div className="relative">
                            <button
                              ref={(el) => { if (el) menuBtnRefs.current[row.id] = el; }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const isSame = menuState.id === row.id;
                                setMenuState({
                                  id: isSame ? null : row.id,
                                  above: false,
                                  top: 0,
                                  left: 0,
                                  ready: false,
                                });
                              }}
                              className="text-gray-500 hover:text-gray-700 text-xl p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                            >
                              ⋮
                            </button>
                            {menuState.id === row.id && ReactDOM.createPortal(
                              <div
                                ref={menuRef}
                                id="row-menu"
                                className={clsx(
                                  'fixed min-w-[12rem] w-max glass rounded-md shadow-lg',
                                  menuState.above ? 'origin-bottom' : 'origin-top',
                                )}
                                style={{
                                  top: menuState.top,
                                  left: menuState.left,
                                  zIndex: 9999,
                                  opacity: menuState.ready ? 1 : 0,
                                  transform: menuState.ready ? 'scale(1)' : 'scale(0.95)',
                                  transition: 'opacity 120ms ease-out, transform 120ms ease-out',
                                }}
                              >
                                <div className="py-1">
                                  {sortedActions
                                    .filter((action) => !(typeof action.hidden === 'function' && action.hidden(row)))
                                    .map((action, idx, visibleActions) => {
                                    const isDisabled = typeof action.disabled === 'function' ? action.disabled(row) : false;
                                    const isLastDestructive = action.isDestructive && idx === visibleActions.length - 1;
                                    return (
                                      <React.Fragment key={idx}>
                                        {isLastDestructive && <hr className="m-1 border-gray-200 dark:border-gray-700" />}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isDisabled) action.onClick(row);
                                            setMenuState({ id: null, above: false, top: 0, left: 0, ready: false });
                                          }}
                                          disabled={isDisabled}
                                          className={clsx(
                                            'flex items-center gap-2 px-4 py-2 text-sm w-full text-left whitespace-nowrap transition',
                                            isDisabled
                                              ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed bg-transparent'
                                              : action.isDestructive
                                                ? 'text-danger-700 hover:bg-danger-100 dark:hover:bg-danger-900/50'
                                                : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                                          )}
                                        >
                                          {action.icon}
                                          {action.label}
                                        </button>
                                      </React.Fragment>
                                    );
                                  })}
                                </div>
                              </div>,
                              document.body
                            )}
                          </div>
                        );
                      }
                      const colWidth = columnWidths[col];

                      // Calculate proper minWidth based on column type
                      const cellMinWidth = colMinWidth(col);

                      return (
                        <td
                          key={col}
                          className={clsx(
                            rowSize === 'compact' ? 'py-1' : rowSize === 'large' ? 'py-3' : 'py-2.5',
                            'text-sm text-gray-900 dark:text-gray-200',
                            col === 'Select' ? 'px-0' : 'px-1',
                            colIdx === 0 && !enableSelection && 'pl-4',
                            hiddenColumns.has(col) && 'hidden lg:table-cell',
                            col === 'Select' && !resizableColumns && 'w-12',
                            col === 'Select' && 'text-center',
                            col === 'Actions' && 'text-center',
                            resizableColumns && col === 'Actions' && 'text-center',
                            // Enforce single-line with ellipsis on all data cells
                            col !== 'Actions' && col !== 'Select' && 'truncate whitespace-nowrap overflow-hidden'
                          )}
                          style={isFixedColumn(col)
                            ? { width: `${fixedColumnWidth(col)}px`, minWidth: `${fixedColumnWidth(col)}px`, maxWidth: `${fixedColumnWidth(col)}px`, flexShrink: 0, ...(col === 'Select' ? { paddingLeft: '0px', paddingRight: '0px' } : {}) }
                            : col === flexColumn ? { minWidth: `${cellMinWidth}px` }
                            : colWidth ? { width: `${colWidth}px`, minWidth: `${cellMinWidth}px`, maxWidth: `${colMaxWidth(col)}px`, flexShrink: 0 } : { maxWidth: `${colMaxWidth(col)}px` }
                          }
                          title={typeof cellContent === 'string' ? cellContent : undefined}
                        >
                          {cellContent}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {isLoadingMore && <Skeleton variant="table" rows={2} />}
      </div>
      {/* Both pagers live outside the scroll region on purpose: they are
          chrome, so they stay put while the rows move, the same way the head
          does. Inside it, a `sticky bottom-0` bar is only as wide as the TABLE
          — on a list wider than its box it slid sideways with the horizontal
          scroll. Same padding as the pager below so every list foot aligns. */}
      {hasMore && (
        <div className="shrink-0 border-t border-hairline px-3 py-2 text-center">
          <Button
            variant="outline"
            size="small"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="inline-flex items-center gap-2"
          >
            {isLoadingMore ? (
              <>
                <Spinner size="glyph" className="!h-4 !w-4" />
                {t('Loading...')}
              </>
            ) : (
              t('Load More')
            )}
          </Button>
        </div>
      )}
      {pagination && pagination.totalPages > 1 && (
        <TablePagination {...pagination} className="shrink-0" />
      )}
    </div>
  );
};

export default Table;
